import { TaskStep, PracticeForm, AnnouncementDoc } from "./hubTasks";

export type TaskStatus = "todo" | "inprogress" | "done" | "overdue";
export type CampaignStatus = "기획중" | "진행중" | "후반작업" | "완료";

export interface CampaignTask {
  id: string;
  title: string;
  detail: string;
  dueDate: string;
  dDay: number;
  status: TaskStatus;
  assignee: string;
  deliverable?: string;
  approvalTemplateId?: string;
  steps?: TaskStep[];
  caution?: string;
  reference?: string;
  /** 분석해야 할 클라이언트 브리프 / 공고문 */
  announcement?: AnnouncementDoc;
  /** 직접 작성해볼 수 있는 연습 양식 */
  practiceForm?: PracticeForm;
}

export interface CampaignMilestone {
  date: string;
  label: string;
  done: boolean;
}

export interface CampaignMember {
  name: string;
  role: string;
  icon: string;
}

export interface CampaignBudget {
  category: string;
  amount: number;
  note: string;
}

export interface Campaign {
  id: string;
  name: string;
  client: string;
  clientLogo: string;
  budget: number;
  type: string;
  status: CampaignStatus;
  deadline: string;
  pm: string;
  objective: string;
  channels: string[];
  targetAudience: string;
  keyMessage: string;
  tasks: CampaignTask[];
  milestones: CampaignMilestone[];
  team: CampaignMember[];
  budgetBreakdown: CampaignBudget[];
  brief: string;
}

export const campaigns: Campaign[] = [
  {
    id: "c1",
    name: "하이트진로 여름 SNS 캠페인",
    client: "하이트진로",
    clientLogo: "ri-cup-line",
    budget: 5000,
    type: "SNS",
    status: "기획중",
    deadline: "2026.06.01",
    pm: "박소현 AE",
    objective: "2030세대 여름 음용 트렌드 공략 및 브랜드 친밀도 +15% 향상",
    channels: ["인스타그램", "틱톡", "유튜브 쇼츠"],
    targetAudience: "20~35세 / 도시 거주 직장인·대학생 / 여름 레저·캠핑 관심군",
    keyMessage: "청량한 여름의 시작은 진로와 함께 — #여름진로챌린지",
    brief:
      "하이트진로의 여름 시즌 SNS 캠페인입니다. 인플루언서 협업 콘텐츠와 UGC(사용자 생성 콘텐츠) 챌린지를 중심으로 진행하며, 틱톡·인스타그램 릴스에서의 바이럴 확산을 목표로 합니다. AE는 클라이언트 오리엔테이션부터 최종 성과 보고서까지 전 프로세스를 관리합니다.",
    tasks: [
      {
        id: "t1-1",
        title: "클라이언트 OT 미팅 자료 준비",
        detail: "하이트진로 마케팅팀과의 오리엔테이션 미팅 자료 작성. 캠페인 목표 KPI, 타임라인 초안, 예산 배분안 포함.",
        dueDate: "2026.03.31",
        dDay: 4,
        status: "inprogress",
        assignee: "박소현 AE (나)",
        deliverable: "OT 미팅 PPT (15슬라이드)",
        steps: [
          { num: 1, title: "클라이언트 사전 조사", detail: "하이트진로 최근 3년 마케팅 캠페인 사례와 경쟁사(오비맥주·하이네켄) 전략을 미리 분석합니다. 의사결정권자가 데이터 중심인지, 크리에이티브 중심인지 파악해 PPT 톤을 맞추세요.", tip: "클라이언트 홈페이지 뉴스룸, 미디어 보도, 링크드인에서 담당자 커리어 파악 → 선호하는 소통 방식 추측 가능" },
          { num: 2, title: "KPI 초안 설정 (3단계 구조)", detail: "인지도(도달수), 참여도(좋아요·공유·챌린지 참여), 전환(구매 의도 리서치) 3단계 KPI를 제안합니다. 수치는 업계 평균 +20% 수준으로 보수적으로 설정해야 첫 미팅에서 공감을 얻기 쉽습니다." },
          { num: 3, title: "PPT 구성 및 발표 흐름", detail: "[배경 분석 → 캠페인 목표 → 타임라인 → 예산 배분 → 다음 단계] 15슬라이드 구성. 첫 OT 미팅이므로 세부 크리에이티브 내용은 담지 말고, '방향성 합의'를 목적으로 간결하게 만드세요.", tip: "슬라이드당 텍스트는 3줄 이하. 클라이언트는 긴 글을 읽지 않습니다." },
        ],
        caution: "OT 미팅은 '우리가 모든 답을 갖고 왔다'는 자세가 아닌 '클라이언트의 진짜 목표를 듣기 위한 자리'입니다. 발표 시간의 40%는 클라이언트가 말할 수 있도록 질문을 준비하세요.",
      },
      {
        id: "t1-2",
        title: "경쟁사 SNS 벤치마킹 리포트",
        detail: "오비맥주, 칭따오 코리아의 최근 6개월 SNS 캠페인 분석. 성과 지표(좋아요·공유·도달)·크리에이티브 방향 정리.",
        dueDate: "2026.04.03",
        dDay: 7,
        status: "todo",
        assignee: "박소현 AE (나)",
        deliverable: "경쟁사 분석 리포트 (A4 5장)",
      },
      {
        id: "t1-3",
        title: "캠페인 기획안 작성",
        detail: "콘셉트 방향성, 챌린지 해시태그 설정, 콘텐츠 유형 및 게시 일정표 초안 작성. 크리에이티브팀과 협의 후 최종본 제출.",
        dueDate: "2026.04.07",
        dDay: 11,
        status: "todo",
        assignee: "박소현 AE (나)",
        deliverable: "캠페인 기획안 PPT + 콘텐츠 캘린더",
        approvalTemplateId: "tmpl9",
        announcement: {
          title: "하이트진로 2026 여름 SNS 캠페인 클라이언트 브리프",
          issuer: "하이트진로 마케팅팀",
          issueDate: "2026.03.20",
          deadline: "2026.04.07 18:00 (기획안 1차 제출)",
          sections: [
            {
              heading: "캠페인 배경 및 목표",
              content: "하이트진로는 2030세대 핵심 소비층 재공략을 위해 2026년 여름 시즌 SNS 캠페인을 기획하고 있습니다.\n\n목표 KPI:\n• 브랜드 친밀도(Brand Affinity): +15% 향상\n• 캠페인 해시태그 챌린지 참여: 10만건 이상\n• SNS 도달수: 누적 500만 도달\n• 유튜브·인스타그램·틱톡 총 영상 조회수: 300만뷰 이상",
            },
            {
              heading: "타겟 오디언스",
              content: "주요 타겟: 20~35세 도시 거주 직장인·대학생\n관심사: 여름 레저, 캠핑, 바베큐, 홈파티\n행동 특성: SNS 콘텐츠 적극 공유형, 챌린지 참여 경험 있음\n\n서브 타겟: 36~45세 가족 단위 소비자 (BBQ·가족 모임 씬)",
            },
            {
              heading: "제품 정보 및 핵심 메시지",
              content: "주력 제품: 진로이즈백 / 청하 / 하이트제로0.00\n\n핵심 메시지: '청량한 여름의 시작은 진로와 함께'\n금지 표현: 과음·음주운전 조장 표현, 미성년자 연상 콘텐츠, 경쟁사 직접 비교\n\n필수 포함 요소: 브랜드 로고, '#여름진로챌린지' 해시태그, 음주폐해예방 캠페인 문구",
            },
            {
              heading: "집행 채널 및 예산",
              content: "집행 채널:\n① 인스타그램 릴스 (주 채널)\n② 틱톡 챌린지 (바이럴 채널)\n③ 유튜브 쇼츠 (확산 채널)\n\n총 예산: 5,000만원\n• 인플루언서 협업비: 1,800만원 (10인)\n• 영상 제작비: 1,200만원\n• SNS 광고 집행비: 1,500만원\n• 기타 운영비: 500만원\n\n집행 기간: 2026.05.19 ~ 2026.07.31",
            },
            {
              heading: "제출 요구사항",
              content: "1차 기획안 제출: 2026.04.07(화) 18:00까지\n\n필수 포함 내용:\n① 캠페인 콘셉트 및 핵심 아이디어 (차별점 명확히)\n② 채널별 콘텐츠 전략 및 포맷\n③ 인플루언서 선정 기준 및 후보군\n④ 월별·채널별 콘텐츠 캘린더\n⑤ 예산 항목별 배분표\n⑥ KPI 달성 방안 및 측정 방법\n\n형식: PPT 15~20슬라이드 + 엑셀 콘텐츠 캘린더\n담당 AE: 박소현 (sosun.park@agency.co.kr)",
            },
            {
              heading: "참고사항 및 유의사항",
              content: "• 주류 광고 자율규제 기준 반드시 준수 (한국주류산업협회 가이드라인)\n• 18세 미만 출연 금지, 음주 미화 표현 금지\n• 클라이언트 최종 컨펌 없이 인플루언서 계약 체결 금지\n• 기획안 제출 후 수정 요청 시 5영업일 이내 수정본 제출 의무\n• 최종 성과보고서 캠페인 종료 후 2주 이내 제출",
            },
          ],
        },
        practiceForm: {
          title: "하이트진로 여름 SNS 캠페인 기획안",
          subtitle: "클라이언트 제출 전 내부 결재용 · PPT 15~20슬라이드 요약본",
          fields: [
            { id: "ca_concept", label: "1. 캠페인 핵심 아이디어 및 콘셉트", type: "textarea", placeholder: "캠페인의 빅 아이디어를 한 문장으로 표현하고, 경쟁 캠페인과 차별화되는 지점을 설명하세요.\n예) '나만의 청량한 여름 순간' 챌린지 — UGC 중심 버티컬 영상으로 MZ세대 일상 속 진로 음용 씬을 자연스럽게 포착", required: true, maxLength: 600, hint: "클라이언트 브리프의 '핵심 메시지'에 직결되는 아이디어여야 합니다. 단순한 제품 홍보가 아닌 '참여하고 싶은 이유'를 만들어주세요." },
            { id: "ca_hashtag", label: "2. 챌린지 해시태그 및 참여 방법", type: "textarea", placeholder: "해시태그: #여름진로챌린지\n참여 방법: ① 진로와 함께하는 나만의 여름 순간 영상 촬영 → ② 해시태그 태그 후 인스타 릴스·틱톡 업로드 → ③ 베스트 참여자 선정 후 상품 증정", required: true, maxLength: 400, hint: "참여 방법이 복잡하면 챌린지가 퍼지지 않습니다. 3단계 이내, 30초 내 실행 가능한 방식으로 설계하세요." },
            { id: "ca_channel", label: "3. 채널별 콘텐츠 전략", type: "textarea", placeholder: "인스타그램 릴스: 인플루언서 10인 × 2편 = 20편 / 틱톡: 챌린지 시드 영상 5편 (틱톡커 5인 협업) / 유튜브 쇼츠: 메인 광고 영상 1편 + 쇼츠 컷 3편", required: true, maxLength: 500 },
            { id: "ca_influencer", label: "4. 인플루언서 선정 기준 및 후보 유형", type: "textarea", placeholder: "선정 기준: 팔로워 5만~50만 / 참여율 3% 이상 / 주류 협업 이력 없음 / 라이프스타일·푸드·캠핑 카테고리\n후보 유형: 캠핑 브이로거 3인 / 홈파티·요리 인플루언서 3인 / 스포츠·레저 인플루언서 4인", required: true, maxLength: 400 },
            { id: "ca_calendar", label: "5. 월별 콘텐츠 캘린더 요약", type: "textarea", placeholder: "5월: 사전 바이럴 콘텐츠 (챌린지 시드 영상 배포) / 6월: 인플루언서 릴스 주 2~3회 업로드 + 챌린지 확산 / 7월: 베스트 UGC 2차 확산 + 여름 피크 성과 보고", required: true, maxLength: 400 },
            { id: "ca_kpi", label: "6. KPI 달성 방안 및 측정 방법", type: "textarea", placeholder: "도달수 500만: 인플루언서 파급력(평균 팔로워 15만 × 10인 × 추정 도달률 33%) + SNS 광고 부스팅\n챌린지 참여 10만건: 인플루언서 10인 첫 업로드 후 팔로워 리포스팅 + 틱톡 '이 소리 사용' 확산 효과\n측정 도구: 인스타그램 인사이트 / 틱톡 애널리틱스 / 네이버 데이터랩 검색량 트래킹", required: true, maxLength: 500 },
          ],
        },
      },
      {
        id: "t1-4",
        title: "인플루언서 후보 리스트업 (20인)",
        detail: "팔로워 5만~50만 규모의 푸드·라이프스타일·캠핑 인플루언서 20인 후보 선정. 팔로워 수·참여율·이전 주류 협업 이력 포함 분석.",
        dueDate: "2026.04.10",
        dDay: 14,
        status: "todo",
        assignee: "박소현 AE (나)",
        deliverable: "인플루언서 후보 리스트 엑셀",
      approvalTemplateId: "tmpl2",
      },
      {
        id: "t1-5",
        title: "1차 기획안 클라이언트 보고",
        detail: "하이트진로 담당 팀장 보고 미팅. 기획안 발표 후 수정 피드백 수령. 수정사항 정리본 당일 공유.",
        dueDate: "2026.04.14",
        dDay: 18,
        status: "todo",
        assignee: "박소현 AE (나)",
        deliverable: "미팅 노트 + 수정 요청사항 정리",
      },
      {
        id: "t1-6",
        title: "크리에이티브 브리프 전달",
        detail: "디자인팀·영상팀에 크리에이티브 브리프 전달. 비주얼 톤앤매너, 키 메시지, 금지 표현 목록 포함.",
        dueDate: "2026.04.21",
        dDay: 25,
        status: "todo",
        assignee: "박소현 AE (나)",
        deliverable: "크리에이티브 브리프 문서",
        practiceForm: {
          title: "크리에이티브 브리프 작성",
          subtitle: "디자인·영상팀 공유용 · 하이트진로 여름 SNS 캠페인",
          fields: [
            { id: "cb_campaign", label: "캠페인명", type: "text", placeholder: "예: 하이트진로 여름 SNS 캠페인 2026", required: true },
            { id: "cb_client", label: "클라이언트", type: "text", placeholder: "예: 하이트진로", required: true },
            { id: "cb_deadline", label: "제작 마감일", type: "date", required: true },
            { id: "cb_tone", label: "톤앤매너 (분위기·감성)", type: "textarea", placeholder: "비주얼과 카피의 전반적인 분위기를 설명하세요.\n예) 청량하고 생동감 있는 여름 에너지 / 밝고 트렌디한 MZ 감성 / 자연광 중심의 생생한 리얼리티 / 과도한 편집 없는 자연스러운 UGC 스타일", required: true, maxLength: 400, hint: "레퍼런스 영상이나 이미지 URL을 함께 공유하면 훨씬 명확하게 전달됩니다." },
            { id: "cb_key_message", label: "핵심 메시지 (카피라이팅 방향)", type: "text", placeholder: "예: 청량한 여름의 시작은 진로와 함께 — #여름진로챌린지", required: true },
            { id: "cb_required", label: "필수 포함 요소", type: "textarea", placeholder: "반드시 들어가야 할 요소를 나열하세요.\n예) 브랜드 로고 (우하단 고정) / 제품 이미지 (진로이즈백) / 해시태그 #여름진로챌린지 / 음주폐해예방 캠페인 문구 (영상 말미 2초)", required: true, maxLength: 400 },
            { id: "cb_forbidden", label: "금지 표현 / 금지 요소", type: "textarea", placeholder: "반드시 피해야 할 표현·이미지·씬을 명확히 기술하세요.\n예) 미성년자 연상 모델 금지 / 음주운전·과음 조장 씬 금지 / 경쟁사 제품 노출 금지 / 어둡거나 분위기 처진 씬 금지 / 19금 표현 금지", required: true, maxLength: 400 },
            { id: "cb_format", label: "산출물 포맷 및 사이즈", type: "textarea", placeholder: "제작물의 포맷과 해상도를 명시하세요.\n예) 인스타 릴스: 9:16 / 1080×1920px / 15~30초\n유튜브 쇼츠: 9:16 / 최대 60초\n스틸 이미지: 1:1 / 1080×1080px (피드용)", required: true, maxLength: 300 },
          ],
        },
      },
    ],
    milestones: [
      { date: "03.31", label: "클라이언트 OT 미팅", done: false },
      { date: "04.14", label: "1차 기획안 보고", done: false },
      { date: "04.28", label: "크리에이티브 시안 확정", done: false },
      { date: "05.12", label: "인플루언서 계약 완료", done: false },
      { date: "05.19", label: "캠페인 론칭", done: false },
      { date: "06.01", label: "중간 성과 보고", done: false },
    ],
    team: [
      { name: "박소현", role: "AE (나) — 전체 진행 총괄", icon: "ri-user-star-line" },
      { name: "최지훈", role: "CD — 크리에이티브 방향 결정", icon: "ri-paint-brush-line" },
      { name: "김아름", role: "디자이너 — 비주얼 시안 제작", icon: "ri-image-line" },
      { name: "이태양", role: "영상PD — 쇼츠/릴스 영상 제작", icon: "ri-video-line" },
      { name: "정유진", role: "미디어플래너 — 광고 집행 설계", icon: "ri-bar-chart-line" },
    ],
    budgetBreakdown: [
      { category: "인플루언서 협업비", amount: 1800, note: "10인 × 평균 180만원" },
      { category: "영상 제작비", amount: 1200, note: "숏폼 3편 + 메인 영상 1편" },
      { category: "SNS 광고 집행비", amount: 1500, note: "인스타·틱톡·유튜브 부스팅" },
      { category: "기타 운영비", amount: 500, note: "미팅비·스튜디오·소품" },
    ],
  },
  {
    id: "c2",
    name: "서울전자 신제품 론칭 캠페인",
    client: "서울전자",
    clientLogo: "ri-smartphone-line",
    budget: 15000,
    type: "ATL+BTL",
    status: "진행중",
    deadline: "2026.04.30",
    pm: "김지수 AE",
    objective: "갤Z 시리즈 신제품 출시 인지도 80% 달성 및 사전예약 10만건",
    channels: ["TV", "옥외광고", "유튜브", "네이버 DA"],
    targetAudience: "25~45세 / 얼리어답터 · 프리미엄 스마트폰 교체 수요층",
    keyMessage: "접고 펼치는 삶의 방식 — 서울전자 Z폴드7",
    brief:
      "서울전자 플래그십 폴더블폰 Z폴드7 론칭 캠페인. TV CF·OOH(지하철·버스 쉘터)·디지털 통합 ATL+BTL 캠페인입니다. AE는 각 채널별 소재 진행 현황을 클라이언트에 주 2회 보고하며, 매체 스케줄 조율이 핵심 업무입니다.",
    tasks: [
      {
        id: "t2-1",
        title: "TV CF 최종 시안 클라이언트 승인",
        detail: "30초 CF 3개 버전 중 최종본 1개 선택. 클라이언트 법무팀 심의 통과 여부 확인 필수.",
        dueDate: "2026.03.28",
        dDay: 1,
        status: "inprogress",
        assignee: "김지수 AE",
        deliverable: "CF 최종 확인서 서명본",
      },
      {
        id: "t2-2",
        title: "OOH 소재 8종 납품",
        detail: "지하철 역사(강남·홍대·신촌·잠실) 및 버스 쉘터 4개소 디스플레이 광고 소재. 사이즈별 레이아웃 8종 납품.",
        dueDate: "2026.04.02",
        dDay: 6,
        status: "todo",
        assignee: "김지수 AE",
        deliverable: "옥외광고 소재 패키지 ZIP",
      },
      {
        id: "t2-3",
        title: "주간 진행보고서 제출 (3차)",
        detail: "3주차 캠페인 집행 현황, 미디어 도달 중간 집계, 이슈사항 정리. 매주 금요일 오전 10시까지 클라이언트 발송.",
        dueDate: "2026.04.04",
        dDay: 8,
        status: "todo",
        assignee: "김지수 AE",
        deliverable: "주간보고서 PDF",
      approvalTemplateId: "tmpl2",
      },
    ],
    milestones: [
      { date: "03.28", label: "TV CF 시안 확정", done: false },
      { date: "04.05", label: "OOH 소재 납품", done: false },
      { date: "04.15", label: "디지털 광고 집행 시작", done: false },
      { date: "04.22", label: "TV 방영 시작", done: false },
      { date: "04.30", label: "론칭 미디어 버즈 보고", done: false },
    ],
    team: [
      { name: "김지수", role: "AE — 전체 진행 총괄", icon: "ri-user-star-line" },
      { name: "오현석", role: "CD — TV CF 크리에이티브", icon: "ri-paint-brush-line" },
      { name: "강나리", role: "매체팀 — TV·OOH 매체 집행", icon: "ri-tv-line" },
      { name: "이민준", role: "디지털팀 — 유튜브·DA 운영", icon: "ri-global-line" },
    ],
    budgetBreakdown: [
      { category: "TV 매체비", amount: 7000, note: "지상파 3사 + 케이블 황금시간대" },
      { category: "OOH 집행비", amount: 3000, note: "서울 주요 8개소 4주" },
      { category: "디지털 광고비", amount: 3500, note: "유튜브 프리롤 + 네이버 DA" },
      { category: "제작비", amount: 1500, note: "CF 제작 + 소재 후반 작업" },
    ],
  },
  {
    id: "c3",
    name: "루나&스타즈 MV 제작",
    client: "K-ArtPEN 레이블",
    clientLogo: "ri-music-2-line",
    budget: 8000,
    type: "영상제작",
    status: "후반작업",
    deadline: "2026.04.15",
    pm: "이민준 PD",
    objective: "1st EP Starlight 타이틀곡 MV 제작 및 유튜브 공개 후 7일 내 1,000만뷰 달성",
    channels: ["유튜브", "멜론", "스포티파이"],
    targetAudience: "15~28세 K-POP 팬덤 국내외 / 특히 일본·동남아 팬층",
    keyMessage: "별빛처럼 빛나는 순간 — 루나&스타즈 'Starlight' MV",
    brief:
      "루나&스타즈의 데뷔 타이틀곡 MV 제작 프로젝트. 현재 후반 편집 단계. VFX 합성, 색보정(Color Grading), 자막 다국어 삽입(한/일/영) 작업 중입니다. 유튜브 최적화 썸네일 및 SEO 태그 작업도 병행해야 합니다.",
    tasks: [
      {
        id: "t3-1",
        title: "VFX 합성 시안 2차 확인",
        detail: "VFX 업체에서 납품된 2차 합성본 확인. 스타 파티클 효과·조명 합성 퀄리티 체크 후 수정 리스트 전달.",
        dueDate: "2026.03.30",
        dDay: 3,
        status: "inprogress",
        assignee: "이민준 PD",
        deliverable: "VFX 수정 요청서",
      },
      {
        id: "t3-2",
        title: "색보정 최종 확인 및 마스터본 승인",
        detail: "컬러리스트 작업본 최종 확인. 유튜브·HDTV·모바일 화면별 색감 점검 후 아티스트·레이블 CEO 승인 수령.",
        dueDate: "2026.04.08",
        dDay: 12,
        status: "todo",
        assignee: "이민준 PD",
        deliverable: "마스터본 승인서",
      },
      {
        id: "t3-3",
        title: "다국어 자막 제작 (한/일/영)",
        detail: "가사 번역 감수 완료된 자막 파일을 편집본에 삽입. 타이밍 일치 여부 재확인 필수.",
        dueDate: "2026.04.10",
        dDay: 14,
        status: "todo",
        assignee: "이민준 PD",
        deliverable: "자막 삽입 완료본",
      },
    ],
    milestones: [
      { date: "03.20", label: "촬영 완료", done: true },
      { date: "03.30", label: "VFX 2차 납품", done: false },
      { date: "04.08", label: "마스터본 확정", done: false },
      { date: "04.12", label: "유튜브 업로드 예약", done: false },
      { date: "04.15", label: "MV 공식 공개", done: false },
    ],
    team: [
      { name: "이민준", role: "PD — 전체 후반작업 총괄", icon: "ri-user-star-line" },
      { name: "서예진", role: "편집감독 — 영상 편집", icon: "ri-scissors-cut-line" },
      { name: "최민석", role: "컬러리스트 — 색보정", icon: "ri-palette-line" },
      { name: "홍승완", role: "VFX — 시각효과 합성", icon: "ri-magic-line" },
    ],
    budgetBreakdown: [
      { category: "촬영 제작비", amount: 3500, note: "스튜디오·세트·스태프 (완료)" },
      { category: "후반작업비", amount: 2500, note: "편집·VFX·색보정" },
      { category: "음악 프로덕션", amount: 1200, note: "믹스·마스터링·MR 제작" },
      { category: "유통·마케팅", amount: 800, note: "유튜브 부스팅·SNS 홍보" },
    ],
  },
  {
    id: "c4",
    name: "인스타그램 AR 필터 개발",
    client: "인스타그램 코리아",
    clientLogo: "ri-instagram-line",
    budget: 3500,
    type: "디지털",
    status: "완료",
    deadline: "2026.03.01",
    pm: "김지수 AE",
    objective: "K-POP 콘셉트 AR 필터 출시, 30일 내 사용 50만건 달성",
    channels: ["인스타그램"],
    targetAudience: "10~25세 인스타그램 활성 사용자 / K-POP 팬",
    keyMessage: "나도 아이돌처럼 — 인스타그램 K-POP AR 필터",
    brief:
      "인스타그램 코리아 의뢰 K-POP 콘셉트 AR 필터 제작 프로젝트. 이미 납품 완료된 프로젝트입니다. 최종 성과: 사용 38만건(목표 대비 76% 달성), 공유 12만건, 스토어 노출 180만회.",
    tasks: [
      {
        id: "t4-1",
        title: "최종 성과 보고서 제출",
        detail: "캠페인 종료 30일 성과 지표 정리. 사용자 데이터 분석 포함.",
        dueDate: "2026.03.01",
        dDay: -25,
        status: "done",
        assignee: "김지수 AE",
        deliverable: "성과 보고서 PDF",
      },
    ],
    milestones: [
      { date: "02.01", label: "AR 필터 개발 시작", done: true },
      { date: "02.20", label: "베타 테스트", done: true },
      { date: "03.01", label: "공식 배포 완료", done: true },
    ],
    team: [
      { name: "김지수", role: "AE — 총괄", icon: "ri-user-star-line" },
      { name: "이현아", role: "AR 개발자 — 필터 구현", icon: "ri-code-line" },
      { name: "오지민", role: "UX 디자이너 — 인터랙션 설계", icon: "ri-artboard-line" },
    ],
    budgetBreakdown: [
      { category: "AR 기술 개발", amount: 2000, note: "Meta Spark 전문 개발" },
      { category: "비주얼 디자인", amount: 800, note: "3D 에셋·애니메이션 제작" },
      { category: "QA·배포", amount: 700, note: "디바이스 호환 테스트" },
    ],
  },
];
